/**
 * Motor de coach-marks (spotlight + popover), vanilla, sin dependencias.
 * Lifteado del bundle de diseño (§C/§2). Se monta UNA capa `.coachmark` dentro
 * de un contenedor `position:relative` y posiciona spotlight + popover desde el
 * rect del ancla. El dimmer es una capa propia (`.coachmark-scrim`, sin eventos)
 * con `clip-path`: huecos redondeados sobre el spotlight y sobre los `lit` (en
 * /conversation, el mic y la lamparita habilitados), que se ven iluminados para
 * que se note que se pueden tocar (Emmanuel, 19/10). Antes el dimmer era el
 * `box-shadow` gigante del spotlight, que sólo deja un hueco.
 *
 * El backdrop (el que cierra con un tap afuera) SÍ lleva `clip-path`, pero sólo
 * para el hit-testing — es invisible: deja AGUJEROS sobre el ancla y sobre los
 * `passThrough` (en /conversation, el composer entero), así el control real
 * debajo sigue usable. Sin esto el backdrop, `inset:0`, se comía el toque del
 * micrófono: el alumno mantenía apretado, hablaba, y no se grababa nada (encargo
 * 114). Este agujero no se ve; los que se ven son los del dimmer.
 *
 * El estilo vive en `coachmark.css`. El contenido en `coachmark-content.ts`.
 */
import {
  COACHMARK_CONTENT,
  COACHMARK_UI,
  coachmarkBody,
  type CoachmarkContent,
  type CoachmarkId,
  type CoachmarkSide,
} from './coachmark-content.js';

/** Clases de los contenedores scrolleables de la conversación (mobile/desktop). */
const SCROLL_SELECTORS = '.app-scroll, .dt-chat';

/**
 * Un mark con contenido propio (fuera del catálogo de /conversation): el recorrido de Progreso de
 * «Armá tu nave» (encargo 117). Mismo estilo y motor; el copy viene del que lo muestra.
 */
export interface CustomCoachmarkContent extends CoachmarkContent {
  body: string;
}

/** Cómo se cerró el mark: con su botón, o tocando afuera / Esc / usando el ancla. */
export type CoachmarkEndHow = 'cta' | 'dismiss';

interface CoachmarkShowBase {
  anchor: HTMLElement;
  /** Override del spotlight redondo (default: el del contenido del mark). */
  round?: boolean;
  /** Override del padding del spotlight en px (default: `--cm-spot-pad` o 10). */
  pad?: number;
  /**
   * Elementos que siguen tocables bajo el mark (además del ancla, que siempre lo
   * es). Un toque ahí actúa sobre el elemento Y cierra el mark (como visto): el
   * user ya está usando el control.
   */
  passThrough?: ReadonlyArray<HTMLElement>;
  /**
   * Elementos que se ven iluminados (sin el oscurecido) mientras el mark está
   * abierto, con un hueco redondo de su tamaño. Sólo si están habilitados y no se
   * superponen con el spotlight (el ancla ya está iluminada).
   */
  lit?: ReadonlyArray<HTMLElement>;
  /** Se llama cuando el user cierra el mark (Entendido / tap afuera / Esc). */
  onEnd?: (how: CoachmarkEndHow) => void;
  /** Texto del botón (default «Entendido»): un recorrido usa «Siguiente». */
  cta?: string;
}

export type CoachmarkShowOptions = CoachmarkShowBase &
  ({ id: CoachmarkId; content?: undefined } | { id?: undefined; content: CustomCoachmarkContent });

/** Rect en coordenadas del contenedor (px). */
export interface HoleRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function overlaps(a: HoleRect, b: HoleRect): boolean {
  return (
    a.left < b.left + b.width &&
    b.left < a.left + a.width &&
    a.top < b.top + b.height &&
    b.top < a.top + a.height
  );
}

/**
 * Funde los agujeros que se tocan en su caja envolvente. Con `evenodd`, dos
 * agujeros superpuestos vuelven a «llenar» la intersección — justo donde suele
 * estar el ancla (la lamparita vive DENTRO del composer).
 */
export function mergeHoles(holes: ReadonlyArray<HoleRect>): HoleRect[] {
  const out = holes.filter((h) => h.width > 0 && h.height > 0).map((h) => ({ ...h }));
  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i];
        const b = out[j];
        if (!a || !b || !overlaps(a, b)) continue;
        const left = Math.min(a.left, b.left);
        const top = Math.min(a.top, b.top);
        out[i] = {
          left,
          top,
          width: Math.max(a.left + a.width, b.left + b.width) - left,
          height: Math.max(a.top + a.height, b.top + b.height) - top,
        };
        out.splice(j, 1);
        merged = true;
        break outer;
      }
    }
  }
  return out;
}

/** `clip-path` del backdrop: todo el contenedor menos los agujeros (evenodd). */
export function backdropClipPath(
  width: number,
  height: number,
  holes: ReadonlyArray<HoleRect>,
): string {
  const r = (n: number): number => Math.round(n * 10) / 10;
  const outer = `M0 0H${r(width)}V${r(height)}H0Z`;
  const cut = mergeHoles(holes)
    .map((h) => `M${r(h.left)} ${r(h.top)}h${r(h.width)}v${r(h.height)}h${r(-h.width)}Z`)
    .join('');
  return `path(evenodd, '${outer}${cut}')`;
}

/** Hueco del dimmer: un rect con esquinas redondeadas (radio ≤ medio lado). */
export interface ScrimHole extends HoleRect {
  radius: number;
}

/**
 * `clip-path` del dimmer: todo el contenedor menos los huecos (evenodd). Los
 * huecos no se pueden superponer (la intersección volvería a oscurecerse): el
 * caller descarta los que tocan a otro.
 */
export function scrimClipPath(
  width: number,
  height: number,
  holes: ReadonlyArray<ScrimHole>,
): string {
  const r = (n: number): number => Math.round(n * 10) / 10;
  const outer = `M0 0H${r(width)}V${r(height)}H0Z`;
  const cut = holes
    .filter((h) => h.width > 0 && h.height > 0)
    .map((h) => {
      const k = Math.max(0, Math.min(h.radius, h.width / 2, h.height / 2));
      const x = h.left;
      const y = h.top;
      const x2 = h.left + h.width;
      const y2 = h.top + h.height;
      const a = `A${r(k)} ${r(k)} 0 0 1`;
      return (
        `M${r(x + k)} ${r(y)}H${r(x2 - k)}${a} ${r(x2)} ${r(y + k)}V${r(y2 - k)}` +
        `${a} ${r(x2 - k)} ${r(y2)}H${r(x + k)}${a} ${r(x)} ${r(y2 - k)}V${r(y + k)}` +
        `${a} ${r(x + k)} ${r(y)}Z`
      );
    })
    .join('');
  return `path(evenodd, '${outer}${cut}')`;
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
  cx: number;
  cy: number;
}

function rectIn(container: HTMLElement, target: HTMLElement): AnchorRect {
  const c = container.getBoundingClientRect();
  const r = target.getBoundingClientRect();
  return {
    top: r.top - c.top,
    left: r.left - c.left,
    width: r.width,
    height: r.height,
    cx: r.left - c.left + r.width / 2,
    cy: r.top - c.top + r.height / 2,
  };
}

export class Coachmark {
  private readonly container: HTMLElement;
  private layer: HTMLDivElement | null = null;
  private backdrop: HTMLDivElement | null = null;
  private scrim: HTMLDivElement | null = null;
  private spot: HTMLDivElement | null = null;
  private pop: HTMLDivElement | null = null;
  private beak: HTMLSpanElement | null = null;
  private anchor: HTMLElement | null = null;
  private passThrough: ReadonlyArray<HTMLElement> = [];
  private lit: ReadonlyArray<HTMLElement> = [];
  private side: CoachmarkSide = 'above';
  private round = false;
  private pad = 10;
  private onEnd: ((how: CoachmarkEndHow) => void) | null = null;
  private endHow: CoachmarkEndHow = 'dismiss';
  private readonly reflow = (): void => this.position();
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.end();
  };
  // Capture + sin `preventDefault`: el toque sigue su camino al control (el
  // hold del mic arranca en este mismo pointerdown).
  private readonly onPointerDown = (e: PointerEvent): void => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    // Tocar un control desactivado no es usarlo: el mark sigue (si no, quedaba
    // «visto» sin que el control hiciera nada).
    const ctl = t.closest<HTMLButtonElement | HTMLInputElement>('button, input, textarea');
    if (ctl?.disabled) return;
    if (this.anchor?.contains(t) || this.passThrough.some((el) => el.contains(t))) this.end();
  };

  constructor(container: HTMLElement) {
    this.container = container;
  }

  private build(): void {
    if (this.layer) return;
    const layer = document.createElement('div');
    layer.className = 'coachmark';
    layer.setAttribute('role', 'dialog');
    layer.setAttribute('aria-modal', 'false');

    const backdrop = document.createElement('div');
    backdrop.className = 'coachmark-backdrop';
    backdrop.addEventListener('click', () => this.end());

    const scrim = document.createElement('div');
    scrim.className = 'coachmark-scrim';

    const spot = document.createElement('div');
    spot.className = 'coachmark-spot';

    const pop = document.createElement('div');
    pop.className = 'coachmark-pop';

    const beak = document.createElement('span');
    beak.className = 'coachmark-beak';

    const head = document.createElement('div');
    head.className = 'coachmark-head';
    const eyebrow = document.createElement('span');
    eyebrow.className = 'coachmark-eyebrow';
    const ei = document.createElement('span');
    ei.className = 'ei';
    const ec = document.createElement('span');
    ec.className = 'ec';
    eyebrow.append(ei, ec);
    head.append(eyebrow);

    const title = document.createElement('h3');
    title.className = 'coachmark-title';
    const body = document.createElement('p');
    body.className = 'coachmark-body';

    const foot = document.createElement('div');
    foot.className = 'coachmark-foot';
    const cta = document.createElement('button');
    cta.className = 'coachmark-cta';
    cta.textContent = COACHMARK_UI.cta;
    cta.addEventListener('click', () => {
      this.endHow = 'cta';
      this.end();
    });
    foot.append(cta);

    pop.append(beak, head, title, body, foot);
    layer.append(backdrop, scrim, spot, pop);
    this.container.appendChild(layer);

    this.layer = layer;
    this.backdrop = backdrop;
    this.scrim = scrim;
    this.spot = spot;
    this.pop = pop;
    this.beak = beak;
  }

  isOpen(): boolean {
    return this.layer?.classList.contains('is-open') ?? false;
  }

  show(opts: CoachmarkShowOptions): void {
    this.build();
    const { layer, spot, pop } = this;
    if (!layer || !spot || !pop) return;

    this.anchor = opts.anchor;
    this.passThrough = opts.passThrough ?? [];
    this.lit = opts.lit ?? [];
    this.onEnd = opts.onEnd ?? null;

    const content = opts.content ?? COACHMARK_CONTENT[opts.id];
    this.endHow = 'dismiss';
    this.side = content.side;
    this.round = opts.round ?? content.round;
    this.pad =
      opts.pad ??
      (parseFloat(getComputedStyle(this.container).getPropertyValue('--cm-spot-pad')) || 10);

    layer.className = `coachmark cm-accent-${content.accent}`;
    spot.className = `coachmark-spot${this.round ? ' round' : ''}`;

    const ei = pop.querySelector<HTMLElement>('.coachmark-eyebrow .ei');
    const ec = pop.querySelector<HTMLElement>('.coachmark-eyebrow .ec');
    const title = pop.querySelector<HTMLElement>('.coachmark-title');
    const bodyEl = pop.querySelector<HTMLElement>('.coachmark-body');
    if (ei) ei.innerHTML = content.icon;
    if (ec) ec.textContent = content.eyebrow;
    if (title) title.textContent = content.title;
    if (bodyEl) bodyEl.textContent = opts.content ? opts.content.body : coachmarkBody(opts.id);
    const ctaEl = pop.querySelector<HTMLButtonElement>('.coachmark-cta');
    if (ctaEl) ctaEl.textContent = opts.cta ?? COACHMARK_UI.cta;

    this.revealAnchor();
    this.position();
    // Commitea el estado cerrado (opacity:0 + transform) con un reflow forzado,
    // DESPUÉS flipea a open para que la transición juegue. Determinista — no
    // depende del timing de rAF ni del foreground del iframe.
    void layer.offsetHeight;
    layer.classList.add('is-open');
    this.position();
    try {
      pop.querySelector<HTMLButtonElement>('.coachmark-cta')?.focus({ preventScroll: true });
    } catch {
      /* focus best-effort */
    }
    window.addEventListener('resize', this.reflow);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('pointerdown', this.onPointerDown, true);
  }

  private revealAnchor(): void {
    if (!this.anchor) return;
    const sp = this.anchor.closest<HTMLElement>(SCROLL_SELECTORS);
    if (!sp) return;
    const a = rectIn(sp, this.anchor);
    const target = a.top + sp.scrollTop - (sp.clientHeight / 2 - a.height / 2);
    sp.scrollTop = Math.max(0, target);
  }

  private position(): void {
    const { anchor, spot, pop, beak, backdrop, scrim, layer, container } = this;
    if (!anchor || !spot || !pop) return;
    const pad = this.pad;
    const a = rectIn(container, anchor);
    const cw = container.clientWidth;
    const ch = container.clientHeight;

    let sTop: number;
    let sLeft: number;
    let sW: number;
    let sH: number;
    if (this.round) {
      const d = Math.max(a.width, a.height) + pad * 2;
      sW = d;
      sH = d;
      sLeft = a.cx - d / 2;
      sTop = a.cy - d / 2;
    } else {
      sLeft = a.left - pad;
      sTop = a.top - pad;
      sW = a.width + pad * 2;
      sH = a.height + pad * 2;
    }
    Object.assign(spot.style, {
      left: `${sLeft}px`,
      top: `${sTop}px`,
      width: `${sW}px`,
      height: `${sH}px`,
    });

    if (backdrop) {
      const holes: HoleRect[] = [
        { left: sLeft, top: sTop, width: sW, height: sH },
        ...this.passThrough.filter((el) => el.isConnected).map((el) => rectIn(container, el)),
      ];
      backdrop.style.setProperty('clip-path', backdropClipPath(cw, ch, holes));
    }

    if (scrim) {
      const spotRadius = this.round
        ? sW / 2
        : parseFloat(getComputedStyle(layer ?? container).getPropertyValue('--cm-spot-radius')) ||
          16;
      const holes: ScrimHole[] = [
        { left: sLeft, top: sTop, width: sW, height: sH, radius: spotRadius },
      ];
      for (const el of this.lit) {
        if (!el.isConnected || (el as HTMLButtonElement).disabled) continue;
        const a = rectIn(container, el);
        const hole = { ...a, radius: Math.min(a.width, a.height) / 2 };
        if (holes.some((h) => overlaps(h, hole))) continue;
        holes.push(hole);
      }
      scrim.style.setProperty('clip-path', scrimClipPath(cw, ch, holes));
    }

    const popW = pop.offsetWidth;
    const popH = pop.offsetHeight;
    const GAP = 16;
    const M = 14;
    let side = this.side;
    const spotTop = sTop;
    const spotBottom = sTop + sH;
    if (side === 'above' && spotTop - GAP - popH < M) side = 'below';
    if (side === 'below' && spotBottom + GAP + popH > ch - M) side = 'above';
    const pTop = side === 'above' ? spotTop - GAP - popH : spotBottom + GAP;
    let pLeft = a.cx - popW / 2;
    pLeft = Math.max(M, Math.min(pLeft, cw - popW - M));
    Object.assign(pop.style, { top: `${pTop}px`, left: `${pLeft}px` });
    pop.classList.toggle('above', side === 'above');
    pop.classList.toggle('below', side === 'below');

    if (beak) {
      const bx = Math.max(16, Math.min(a.cx - pLeft, popW - 16));
      beak.style.left = `${bx - 6}px`;
    }
  }

  /** Cierra como si lo hubiera cerrado el user: dispara `onEnd` (lo da por visto). */
  dismiss(): void {
    this.end();
  }

  private end(): void {
    if (!this.layer) return;
    this.layer.classList.remove('is-open');
    window.removeEventListener('resize', this.reflow);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onPointerDown, true);
    const cb = this.onEnd;
    const how = this.endHow;
    this.onEnd = null;
    this.endHow = 'dismiss';
    window.setTimeout(() => {
      if (cb) cb(how);
    }, 200);
  }

  /** Cierra sin disparar callbacks (p.ej. al cambiar de contexto). */
  hide(): void {
    if (!this.layer) return;
    this.layer.classList.remove('is-open');
    window.removeEventListener('resize', this.reflow);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onPointerDown, true);
    this.onEnd = null;
  }

  destroy(): void {
    window.removeEventListener('resize', this.reflow);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onPointerDown, true);
    if (this.layer) {
      this.layer.remove();
      this.layer = null;
    }
  }
}
